import { z } from 'zod';

// ─── Nexus Token ────────────────────────────────────────────────────────────
// Zod contract for the Nexus-signed JWT payload.
// Firebase handles identity (login). At token exchange the API mints a
// Nexus JWT that identifies the user. All downstream services verify this
// token — never the raw Firebase token. Access-group memberships are NOT in
// the JWT: a user with ~100 tenants pushed the session cookie past the
// browser's 4 KB limit and was silently logged out (AD-3429). Each service
// resolves memberships server-side from `sub` after verifying the token.

/** Default JWT lifetime in seconds; overridden at runtime by `AUTH_TOKEN_TTL_SECONDS` when set. */
export const NEXUS_JWT_TTL_SECONDS_DEFAULT = 3600 as const;

/** Audience on JWTs minted by External API token exchange. Session tokens omit `aud`. */
export const EXTERNAL_API_JWT_AUDIENCE = 'external-api' as const;

/** Claims a Nexus JWT carries. This is everything that is signed. */
export const NexusTokenClaimsSchema = z.object({
    /** Subject — Firebase UID (or the user id when no provider UID is bound) */
    sub: z.string().min(1),
    /**
     * @deprecated Rollout compatibility only — never read. Replicas and web
     * bundles from before the slim token read memberships from this claim, so
     * it is still signed for one release while it stays small. Every current
     * verifier drops it and resolves memberships server-side.
     */
    acs: z.array(z.string().min(1)).optional(),
    /** User email (from Firebase) */
    email: z.string().email(),
    /**
     * JWT audience. Absent on web session tokens. External API exchanges set
     * `external-api`. Preserved on verify so the BFF can reject that audience.
     */
    aud: z.string().min(1).optional(),
    /** Issued-at timestamp (epoch seconds) */
    iat: z.number(),
    /** Expiration timestamp (epoch seconds) */
    exp: z.number(),
    /** Issuer — always "nexus" for Nexus-signed tokens */
    iss: z.literal('nexus'),
    /** JWT ID — unique per token, useful for revocation */
    jti: z.string().min(1),
});

export type NexusTokenClaims = z.infer<typeof NexusTokenClaimsSchema>;

/**
 * The authenticated caller on a request: verified JWT claims plus the
 * access-group IDs the service resolved server-side for `sub`. `acs` is
 * never read from the token.
 */
export const NexusTokenPayloadSchema = NexusTokenClaimsSchema.extend({
    /** Access-group IDs the user belongs to (each maps to a tenant), resolved server-side */
    acs: z.array(z.string().min(1)),
});

export type NexusTokenPayload = z.infer<typeof NexusTokenPayloadSchema>;

// ─── Firebase Identity ──────────────────────────────────────────────────────
// Minimal shape extracted from a verified Firebase ID token.
// Only used at the /auth/exchange boundary.

export const FirebaseIdentitySchema = z.object({
    /** Firebase UID */
    uid: z.string().min(1),
    /** Email address */
    email: z.string().email().optional(),
    /** Whether Firebase has verified the email */
    emailVerified: z.boolean().optional(),
    /** Auth provider from Firebase token (e.g. "google.com", "password") */
    signInProvider: z.string().optional(),
    /** Google profile photo URL (only present for google.com provider) */
    photoUrl: z.string().url().optional(),
    /** Human-readable name from the identity provider (e.g. Firebase `name`) */
    displayName: z.string().min(1).optional(),
});

export type FirebaseIdentity = z.infer<typeof FirebaseIdentitySchema>;
